import * as React from 'react';
import styled from 'styled-components';
import {Helmet} from 'react-helmet';
import {useRoute} from 'react-router5';
import {toast} from 'react-toastify';
import Container from 'containers/Container';
import Header from 'components/Header';
import HeaderSearch from 'components/HeaderSearch';
import ButtonLink from 'components/ButtonLink';
import {ButtonVariant} from 'components/Button';
import UserStore from 'stores/UserStore';
import useLoading from 'hooks/use-loading';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/isError';
import ControlButton from 'components/ControlButton';
import {IconEdit, IconTrash} from 'icons';
import {TrainingItem, ProgramObject} from '../../api/program/types';
import {deleteProgram, getPrograms} from '../../api/program/requests';
import TrainingPreview from '../../components/TrainingPreview';

export const Inline = styled.div`
  align-items: center;
  grid-auto-flow: column;
  display: inline-grid;
`;

export const PageContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  box-sizing: border-box;
`;

/**
 * List of an trainings.
 * @return {React.ReactElement}
 * @constructor
 */
export default function Trainings(): React.ReactElement {
  const {data, fetch, render} = useLoading<ProgramObject<TrainingItem>[]>();
  const {route, router} = useRoute();

  React.useEffect(() => {
    fetchTrainings();
  }, [route.params.search]);

  /**
   * Fetch trainings.
   */
  function fetchTrainings(): void {
    fetch(() => new Promise((resolve) => (
      getPrograms<TrainingItem>({
        type: 'gym',
        title: route.params.search || undefined,
      }).then((response) => {
        validateResponse(response);

        if (response && !isError(response)) {
          resolve(response);
        }
      })
    )));
  }

  /**
   * This function renders the page header.
   * @return {JSX.Element}
   */
  function renderHeader(): JSX.Element {
    return (
      <Header>
        <HeaderSearch
          inputSearch={{
            placeholder: 'Поиск по программам тренировок',
          }}
          renderAfter={UserStore.isTeacher() ? (
            <ButtonLink href="/trainings/create" variant={ButtonVariant.Primary}>
              Создать программу
            </ButtonLink>
          ) : undefined}
        />
      </Header>
    );
  }

  /**
   * Render content.
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element {
    /**
     * Render controls.
     * @param {ProgramObject<TrainingItem>} training
     * @return {JSX.Element}
     */
    function renderControls(
        training: ProgramObject<TrainingItem>,
    ): JSX.Element {
      /**
       * Render edit.
       * @return {JSX.Element}
       */
      function renderEdit(): JSX.Element {
        /**
         * The click event handler.
         */
        function handleClick(): void {
          router.navigate('trainings.training.edit', {
            trainingId: training.id,
          })
        }

        return (
          <ControlButton onClick={handleClick}>
            <IconEdit />
          </ControlButton>
        );
      }

      /**
       * Render delete.
       * @return {JSX.Element}
       */
      function renderDelete(): JSX.Element {
        /**
         * The click event handler.
         */
        function handleClick(): void {
          deleteProgram<TrainingItem>(training.id).then((response) => {
            validateResponse(response);

            if (response && !isError(response)) {
              toast.success('Программа удалена');
              fetchTrainings();
            }
          });
        }

        return (
          <ControlButton onClick={handleClick}>
            <IconTrash />
          </ControlButton>
        );
      }

      return (
        <Inline>
          {renderEdit()}
          {renderDelete()}
        </Inline>
      );
    }

    return (
      <Container>
        {data?.map((exercise) => (
          <TrainingPreview
            {...exercise}
            renderControls={renderControls(exercise)}
          />
        ))}
      </Container>
    );
  }

  /**
   * Render not found.
   * @return {JSX.Element}
   */
  function renderNotFound(): JSX.Element {
    return (
      <React.Fragment />
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>FitPlace / Программы тренировок</title>
      </Helmet>
      <PageContentContainer>
        {renderHeader()}
        {render(renderContent, renderNotFound)}
      </PageContentContainer>
    </React.Fragment>
  );
}
